import { useMemo, useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { diffWords, Change } from "diff";

/**
 * A ChangeGroup bundles adjacent added/removed diff parts into a single
 * logical change that the user can accept or reject as a unit.
 */
interface ChangeGroup {
    type: 'unchanged' | 'change';
    /** For 'unchanged': the text. For 'change': the removed + added parts. */
    parts: Change[];
    /** Index used as key and for tracking decisions */
    groupIndex: number;
}

export interface InlineDiffViewHandle {
    getComposedText: () => string;
}

interface IInlineDiffViewProps {
    original: string;
    suggested: string;
    onDecisionsChange?: (acceptedCount: number, rejectedCount: number, totalChanges: number) => void;
}

/**
 * Strips markdown formatting from text for clean text-to-text comparison.
 */
function stripMarkdown(text: string): string {
    return text
        .replace(/```[\s\S]*?```/g, '')
        .replace(/`([^`]+)`/g, '$1')
        .replace(/^#{1,6}\s+/gm, '')
        .replace(/\*\*\*(.+?)\*\*\*/g, '$1')
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/\*(.+?)\*/g, '$1')
        .replace(/___(.+?)___/g, '$1')
        .replace(/__(.+?)__/g, '$1')
        .replace(/_(.+?)_/g, '$1')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
        .replace(/^>\s+/gm, '')
        .replace(/^[\s]*[-*+]\s+/gm, '')
        .replace(/^[\s]*\d+\.\s+/gm, '')
        .replace(/^[-*_]{3,}\s*$/gm, '')
        .replace(/~~(.+?)~~/g, '$1')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

/**
 * Groups adjacent added/removed diff parts into logical change groups.
 * Unchanged parts become their own group.
 */
function groupDiffParts(parts: Change[]): ChangeGroup[] {
    const groups: ChangeGroup[] = [];
    let groupIndex = 0;
    let i = 0;

    while (i < parts.length) {
        const part = parts[i];

        if (!part.added && !part.removed) {
            // Unchanged text
            groups.push({ type: 'unchanged', parts: [part], groupIndex: groupIndex++ });
            i++;
        } else {
            // Collect adjacent added/removed parts into one change group
            const changeParts: Change[] = [];
            while (i < parts.length && (parts[i].added || parts[i].removed)) {
                changeParts.push(parts[i]);
                i++;
            }
            groups.push({ type: 'change', parts: changeParts, groupIndex: groupIndex++ });
        }
    }

    return groups;
}

/**
 * INLINE DIFF VIEW COMPONENT with Accept/Reject per Change
 */
const InlineDiffView = forwardRef<InlineDiffViewHandle, IInlineDiffViewProps>(({
    original,
    suggested,
    onDecisionsChange,
}, ref) => {
    const diffParts = useMemo(() => {
        const cleanSuggested = stripMarkdown(suggested);
        return diffWords(original, cleanSuggested);
    }, [original, suggested]);

    const changeGroups = useMemo(() => groupDiffParts(diffParts), [diffParts]);

    // Track accept/reject decisions: key = groupIndex, value = decision
    // All changes default to 'accepted'
    const [decisions, setDecisions] = useState<Map<number, 'accepted' | 'rejected'>>(new Map());

    // Reset decisions when diff changes
    useEffect(() => {
        setDecisions(new Map());
    }, [diffParts]);

    const changeGroupIndices = useMemo(
        () => changeGroups.filter(g => g.type === 'change').map(g => g.groupIndex),
        [changeGroups]
    );

    const totalChanges = changeGroupIndices.length;

    const getDecision = (groupIndex: number): 'accepted' | 'rejected' => {
        return decisions.get(groupIndex) || 'accepted';
    };

    const toggleDecision = (groupIndex: number) => {
        setDecisions(prev => {
            const next = new Map(prev);
            const current = next.get(groupIndex) || 'accepted';
            next.set(groupIndex, current === 'accepted' ? 'rejected' : 'accepted');
            return next;
        });
    };

    const acceptAll = () => {
        const next = new Map<number, 'accepted' | 'rejected'>();
        changeGroupIndices.forEach(idx => next.set(idx, 'accepted'));
        setDecisions(next);
    };

    const rejectAll = () => {
        const next = new Map<number, 'accepted' | 'rejected'>();
        changeGroupIndices.forEach(idx => next.set(idx, 'rejected'));
        setDecisions(next);
    };

    // Notify parent of decision counts
    useEffect(() => {
        if (onDecisionsChange) {
            const rejectedCount = changeGroupIndices.filter(idx => (decisions.get(idx) || 'accepted') === 'rejected').length;
            const acceptedCount = totalChanges - rejectedCount;
            onDecisionsChange(acceptedCount, rejectedCount, totalChanges);
        }
    }, [decisions, changeGroupIndices, totalChanges, onDecisionsChange]);

    /**
     * Compose the final text based on accept/reject decisions.
     * Accepted changes: use added text, drop removed.
     * Rejected changes: use removed text (original), drop added.
     */
    const getComposedText = (): string => {
        let result = '';
        for (const group of changeGroups) {
            if (group.type === 'unchanged') {
                result += group.parts[0].value;
            } else {
                const decision = getDecision(group.groupIndex);
                for (const part of group.parts) {
                    if (decision === 'accepted') {
                        // Keep added, drop removed
                        if (part.added) result += part.value;
                    } else {
                        // Keep removed (original), drop added
                        if (part.removed) result += part.value;
                    }
                }
            }
        }
        return result;
    };

    // Expose getComposedText to parent via ref
    useImperativeHandle(ref, () => ({
        getComposedText,
    }));

    return (
        <div>
            {/* Accept All / Reject All */}
            {totalChanges > 0 && (
                <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-gray-400">
                        {totalChanges} change{totalChanges !== 1 ? 's' : ''}
                    </span>
                    <div className="flex gap-1 ml-auto">
                        <button
                            onClick={acceptAll}
                            className="text-xs px-2 py-0.5 rounded border border-green-300 text-green-700 hover:bg-green-50 transition-colors"
                        >
                            ✓ Accept All
                        </button>
                        <button
                            onClick={rejectAll}
                            className="text-xs px-2 py-0.5 rounded border border-red-300 text-red-700 hover:bg-red-50 transition-colors"
                        >
                            ✗ Reject All
                        </button>
                    </div>
                </div>
            )}

            <div className="p-4 bg-white rounded-md border border-gray-200">
                <div className="text-sm leading-relaxed whitespace-pre-wrap">
                    {changeGroups.map((group) => {
                        if (group.type === 'unchanged') {
                            return <span key={group.groupIndex}>{group.parts[0].value}</span>;
                        }

                        const decision = getDecision(group.groupIndex);
                        const isRejected = decision === 'rejected';

                        return (
                            <span
                                key={group.groupIndex}
                                className={`change-group ${isRejected ? 'rejected' : 'accepted'}`}
                            >
                                {group.parts.map((part, partIdx) => {
                                    if (part.added) {
                                        return (
                                            <span
                                                key={partIdx}
                                                className={isRejected ? 'diff-rejected-added' : 'diff-added'}
                                                title={isRejected ? 'Rejected addition' : 'Added'}
                                            >
                                                {part.value}
                                            </span>
                                        );
                                    }
                                    if (part.removed) {
                                        return (
                                            <span
                                                key={partIdx}
                                                className={isRejected ? 'diff-rejected-removed' : 'diff-removed'}
                                                title={isRejected ? 'Kept (original)' : 'Removed'}
                                            >
                                                {part.value}
                                            </span>
                                        );
                                    }
                                    return <span key={partIdx}>{part.value}</span>;
                                })}
                                <button
                                    onClick={() => toggleDecision(group.groupIndex)}
                                    className={`change-action-btn ${isRejected ? 'change-action-reject' : 'change-action-accept'}`}
                                    title={isRejected ? 'Accept this change' : 'Reject this change'}
                                >
                                    {isRejected ? '↩' : '✗'}
                                </button>
                            </span>
                        );
                    })}
                </div>
            </div>
        </div>
    );
});

InlineDiffView.displayName = 'InlineDiffView';

export default InlineDiffView;
